import { DeviceTypeEnum, SkillEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS, Step } from 'react-joyride';

import { useTestSubmissionStore } from '../../model/test-submissions.hook';
import { ReviewStatus } from '../../model/test-submissions.store';
import { updateUserTutorial } from './api/user-profile';
import { defaultGuidedTourProps } from './defaultGuidedTourProps';

interface ListeningMobileGuidedTourProps {
  setShowOverall: (show?: boolean) => void;
}

export const ListeningMobileGuidedTour = observer((props: ListeningMobileGuidedTourProps) => {
  const { setShowOverall } = props;

  const [tourStepIndex, setTourStepIndex] = React.useState(0);
  const testSubmissionStore = useTestSubmissionStore();
  const guidedListeningSteps: Step[] = React.useMemo(
    () => [
      {
        target: '.section-right',
        title: 'Thời gian làm bài',
        content: `Bạn có ${testSubmissionStore.currentTestSection.duration} phút để hoàn thành mỗi section.`,
        disableBeacon: true,
        placement: 'auto',
        spotlightPadding: 2,
        styles: {
          spotlight: {
            borderRadius: 0,
          },
        },
      },
      {
        target: '.do-test-footer .dol-progress-bar:first-child',
        title: 'Không thể chuyển giữa các section',
        content: 'Trong quá trình làm 1 section, bạn không thể chuyển qua các section khác.',
        placement: 'top-start',
        spotlightPadding: 12,
      },
      {
        target: '.btn-review-submit',
        title: 'Review time',
        content: `Bạn có ${Math.floor(
          testSubmissionStore.reviewTime / 60000
        )}’ để review và sửa lại đáp án của mình. Lúc này thanh review tất cả câu hỏi mới xuất hiện.`,
        placement: 'top-start',
        spotlightPadding: 12,
      },
      {
        target: '.btn-submit-do-test',
        title: 'Nộp bài',
        content: `Bạn chỉ được submit khi nghe hết section ${testSubmissionStore.testSections.length} và bước qua review time.`,
        styles: {
          spotlight: {
            borderRadius: 8,
          },
        },
      },
    ],
    [
      testSubmissionStore.currentTestSection.duration,
      testSubmissionStore.reviewTime,
      testSubmissionStore.testSections.length,
    ]
  );

  React.useEffect(() => {
    testSubmissionStore.tourGuide.setTotalStep(guidedListeningSteps.length);
  }, [testSubmissionStore.tourGuide, guidedListeningSteps.length]);

  const joyRideCallback = React.useCallback(
    (data) => {
      const { action, index, status, type } = data;
      const elmSpotLight: any = document.getElementsByClassName('react-joyride__spotlight')?.[0];
      testSubmissionStore.tourGuide.setTourIndex(index);
      switch (index) {
        case 0: {
          if (elmSpotLight) {
            elmSpotLight.style.right = '0px';
            elmSpotLight.style.width = '100%';
          }
          setShowOverall(false);
          testSubmissionStore.setReviewStatus(ReviewStatus.NOT_STARTED);
          break;
        }
        case 1: {
          if (elmSpotLight) {
            elmSpotLight.style.left = '0px';
            elmSpotLight.style.right = '0px';
            elmSpotLight.style.width = '100%';
            elmSpotLight.style.top = `${Number(elmSpotLight.style.top.replace('px', '')) + 4}px`;
          }
          setShowOverall(false);
          testSubmissionStore.setReviewStatus(ReviewStatus.NOT_STARTED);
          break;
        }
        case 2: {
          if (elmSpotLight) {
            elmSpotLight.style.left = '0px';
            elmSpotLight.style.right = '0px';
            elmSpotLight.style.width = '100%';
            elmSpotLight.style.top = `${Number(elmSpotLight.style.top.replace('px', '')) + 4}px`;
          }
          setShowOverall(false);
          testSubmissionStore.setReviewStatus(ReviewStatus.REVIEWING);
          break;
        }
        case 3: {
          setShowOverall(true);
          testSubmissionStore.setReviewStatus(ReviewStatus.NOT_STARTED);
          break;
        }
        default: {
          setShowOverall(false);
          testSubmissionStore.setReviewStatus(ReviewStatus.NOT_STARTED);
        }
      }

      if (type === EVENTS.STEP_AFTER) {
        let nextIndex = index;
        switch (action) {
          case ACTIONS.NEXT: {
            nextIndex += 1;
            break;
          }
          case ACTIONS.PREV: {
            nextIndex -= 1;
            break;
          }
        }
        setTourStepIndex(nextIndex);
      } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        setShowOverall(false);
        testSubmissionStore.goToFirstQuestionGroup();
        testSubmissionStore.setReviewStatus(ReviewStatus.NOT_STARTED);
        updateUserTutorial({ skill: SkillEnum.LISTENING, deviceType: DeviceTypeEnum.MOBILE });
        testSubmissionStore.tourGuide.finishTourGuide();
      }
    },
    [setShowOverall, testSubmissionStore]
  );

  return (
    <Joyride
      {...defaultGuidedTourProps}
      spotlightPadding={0}
      disableScrollParentFix
      disableScrolling
      run={!testSubmissionStore.tourGuide.isTourGuideFinished}
      callback={joyRideCallback}
      stepIndex={tourStepIndex}
      steps={guidedListeningSteps}
    />
  );
});
